<template>
  <el-dialog
    :model-value="modelValue"
    :title="t('nav.feedback')"
    width="640px"
    class="feedback-dialog"
    @close="handleClose"
  >
    <el-form
      ref="feedbackFormRef"
      class="feedback-form"
      :model="feedbackForm"
      :rules="feedbackRules"
      :label-width="labelWidth"
      :label-suffix="labelSuffix"
      hide-required-asterisk
    >
      <el-form-item :label="t('feedback.problem')" prop="content">
        <el-input
          v-model="feedbackForm.content"
          type="textarea"
          :rows="4"
          resize="none"
          maxlength="1000"
          show-word-limit
          :placeholder="t('feedback.problemPlaceholder')"
        />
      </el-form-item>

      <el-form-item :label="t('feedback.contactEmail')" prop="email">
        <el-input v-model.trim="feedbackForm.email" :placeholder="t('feedback.emailPlaceholder')" />
      </el-form-item>

      <el-form-item :label="t('feedback.attachment')">
        <el-upload
          ref="uploadRef"
          class="feedback-upload"
          action="#"
          accept="image/*"
          :http-request="customUpload"
          name="image"
          :show-file-list="false"
          :limit="9"
          v-model:file-list="fileList"
          :on-change="handleFileChange"
          :on-success="handleUploadSuccess"
          :on-error="handleUploadError"
          :on-exceed="handleFileExceed"
          :before-upload="beforeUpload"
        >
          <div class="upload-preview-list">
            <div v-for="(item, index) in previewList" :key="item.uid" class="preview-item">
              <img class="preview-image" :src="item.previewUrl" :alt="t('feedback.attachment')" />
              <button
                class="remove-image-button"
                type="button"
                :aria-label="t('feedback.removeAttachment')"
                @click.stop="handleFileRemove(index)"
              >
                <el-icon>
                  <Delete />
                </el-icon>
              </button>
            </div>
            <div v-if="previewList.length < 9" class="upload-box">
              <el-icon>
                <Plus />
              </el-icon>
            </div>
          </div>
        </el-upload>
      </el-form-item>
    </el-form>

    <template #footer>
      <div class="dialog-footer">
        <el-button @click="handleClose">{{ t('common.cancel') }}</el-button>
        <el-button type="primary" :loading="submitting" @click="handleSubmit">
          {{ t('common.submit') }}
        </el-button>
      </div>
    </template>
  </el-dialog>
</template>

<script setup>
import { computed, onBeforeUnmount, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { Delete, Plus } from '@element-plus/icons-vue'
import { ElMessage, ElMessageBox, ElLoading } from 'element-plus'
import axios from '@/utils/request'
import store from '@/store'

defineProps({
  modelValue: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue'])
const { t, locale } = useI18n()

const feedbackFormRef = ref(null)
const uploadRef = ref(null)
const submitting = ref(false)
const loadingInstance = ref(null)
const fileList = ref([])
const previewList = ref([])

const labelWidth = computed(() => (locale.value === 'en-US' ? '170px' : '150px'))
const labelSuffix = computed(() => (locale.value === 'zh-CN' ? '：' : ':'))

const feedbackForm = reactive({
  content: '',
  email: ''
})

const feedbackRules = reactive({
  content: [{ required: true, message: t('feedback.pleaseInputProblem'), trigger: 'blur' }],
  email: [
    { required: true, message: t('feedback.pleaseInputEmail'), trigger: 'blur' },
    {
      pattern: /^[a-zA-Z0-9_.-]+@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.[a-zA-Z0-9]{2,6}$/,
      message: t('feedback.invalidEmail'),
      trigger: 'blur'
    }
  ]
})

const clearPreviewList = () => {
  previewList.value.forEach(item => URL.revokeObjectURL(item.previewUrl))
  previewList.value = []
}

const resetForm = () => {
  feedbackForm.content = ''
  feedbackForm.email = ''
  clearPreviewList()
  fileList.value = []
  uploadRef.value?.clearFiles()
  feedbackFormRef.value?.clearValidate()
}

const closeDialog = () => {
  emit('update:modelValue', false)
}

const handleClose = () => {
  resetForm()
  closeDialog()
}

const handleFileChange = (uploadFile, uploadFiles) => {
  const file = uploadFile.raw
  if (!file) return

  if (!previewList.value.some(item => item.uid === uploadFile.uid)) {
    previewList.value.push({
      uid: uploadFile.uid,
      previewUrl: URL.createObjectURL(file),
      uploadedUrl: ''
    })
  }
  fileList.value = [...uploadFiles]
}

const handleFileRemove = (index) => {
  if (index !== undefined && index >= 0) {
    const [removedItem] = previewList.value.splice(index, 1)
    URL.revokeObjectURL(removedItem.previewUrl)
    fileList.value = fileList.value.filter(file => file.uid !== removedItem.uid)
  } else {
    clearPreviewList()
    fileList.value = []
  }
}

const beforeUpload = (file) => {
  const validExtensions = ['image/jpeg', 'image/png', 'image/jpg']
  if (!validExtensions.includes(file.type)) {
    ElMessage.error(t('feedback.invalidImage'))
    return false
  }
  return true
}

const customUpload = async (options) => {
  const { file, onSuccess, onError } = options
  try {
    loadingInstance.value = ElLoading.service({
      fullscreen: true,
      text: t('common.loading')
    })
    const formData = new FormData()
    formData.append('image', file)

    const res = await axios.post('/api/upload_image/', formData)

    if (res.code === 500) {
      ElMessage.error(res.msg)
      onError(res)
    } else {
      onSuccess(res, file)
    }
  } catch (error) {
    console.error('上传失败:', error)
    onError(error)
  } finally {
    loadingInstance.value?.close()
  }
}

const handleUploadSuccess = (response, file) => {
  if (response && response.url) {
    const previewItem = previewList.value.find(item => item.uid === file.uid)
    if (previewItem) {
      previewItem.uploadedUrl = response.url
    }
  }
}

const handleUploadError = (error) => {
  ElMessage.error(t('feedback.uploadFail'))
  console.error('上传错误:', error)
}

const handleFileExceed = () => {
  ElMessage.warning(t('feedback.maxImages'))
}

const handleSubmit = () => {
  feedbackFormRef.value.validate(async (valid) => {
    if (!valid) return

    submitting.value = true
    try {
      await axios.post('/api/yijianfankui/', {
        neirong: feedbackForm.content,
        youxiang: feedbackForm.email,
        user_id: store.state.userId,
        files: previewList.value.map(item => item.uploadedUrl).filter(Boolean)
      })
      handleClose()
      ElMessageBox.alert(t('feedback.submitSuccess'), t('common.tip'), {
        confirmButtonText: t('common.confirm'),
        type: 'success'
      }).catch(() => {})
    } catch (error) {
      ElMessageBox.alert(t('feedback.submitFail'), t('common.tip'), {
        confirmButtonText: t('common.confirm'),
        type: 'error'
      }).catch(() => {})
    } finally {
      submitting.value = false
    }
  })
}

onBeforeUnmount(() => {
  clearPreviewList()
})
</script>

<style scoped lang="scss">
.feedback-form {
  padding: 6px 16px 0 0;

  :deep(.el-form-item) {
    margin-bottom: 18px;
  }

  :deep(.el-form-item__label) {
    color: #333333;
    font-size: 16px;
    overflow: visible;
    white-space: nowrap;
  }

  :deep(.el-textarea__inner),
  :deep(.el-input__wrapper) {
    border-radius: 6px;
  }
}

.feedback-upload {
  width: 100%;
}

.upload-preview-list {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.preview-item {
  position: relative;
  width: 120px;
  height: 120px;
}

.upload-box {
  width: 120px;
  height: 120px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px dashed #dcdfe6;
  border-radius: 6px;
  color: #8c939d;
  font-size: 42px;
  background: #ffffff;
  overflow: hidden;
  cursor: pointer;

  &:hover {
    border-color: #409eff;
    color: #409eff;
  }
}

.preview-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 6px;
}

.remove-image-button {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 24px;
  height: 24px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.5);
  color: #ffffff;
  border: none;
  cursor: pointer;

  &:hover {
    background: rgba(0, 0, 0, 0.7);
  }
}

.remove-image-button {
  width: 28px;
  height: 28px;
  position: absolute;
  top: 6px;
  right: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 50%;
  color: #ffffff;
  background: rgba(0, 0, 0, 0.55);
  cursor: pointer;
  font-size: 15px;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

@media (max-width: 680px) {
  .feedback-form {
    padding-right: 0;

    :deep(.el-form-item) {
      display: block;
    }

    :deep(.el-form-item__label) {
      justify-content: flex-start;
      width: 100% !important;
      margin-bottom: 8px;
    }
  }
}
</style>
